import { deriveAudioRefs } from '../../shared/audio-catalog';
import type { AudioTaxonomy } from '../../shared/audio-catalog';
import { readItemField } from './item-field-reader';
import { phpJsonString, phpJsonTruthy } from './php-json-values';
import { fromBaseClone, fromBaseRecord, FromBaseStageMissing } from './from-base-context';
import type { FromBaseRow as Row } from './from-base-context';

export interface InventoryMediaMetadata {
    audioTaxonomy: AudioTaxonomy;
    /** Complete static leveled-status registry, including explicit nonleveled misses. */
    statusBases: Readonly<Record<string, string | false | null>>;
}
export interface InventoryVideoContext {
    /** Complete SELECT result for each held id, including ids with no videos. */
    lookupIds: readonly string[];
    rows: readonly Row[];
    cdnUrl: string;
}
type LayerReader = (row: Row, name: string) => unknown;

/** Native ft_audio_refs_ensure boundary. Normalize through the compile readers
 * before handing the three sound layers to the existing shared taxonomy twin. */
export function constructInventoryDefinitionAudio(source: Row, metadata: InventoryMediaMetadata, value: LayerReader,
    hasLayer: (row: Row, name: string) => boolean): Row {
    for (const name of ['hit', 'swing', 'damage', 'spell_miss', 'piercing', 'daggers', 'dagger_piercing'] as const)
        if (metadata.audioTaxonomy?.[name] == null) throw new FromBaseStageMissing('definition-audio-taxonomy', name);
    const result = fromBaseClone(source);
    const id = String(readItemField(source, 'item_id') ?? '');
    const type = String(readItemField(source, 'item_type') ?? '');
    const wc = hasLayer(source, 'weapon_classification') ? phpJsonString(value(source, 'weapon_classification') ?? '') : null;
    const dt = hasLayer(source, 'damage_type') ? phpJsonString(value(source, 'damage_type') ?? '') : null;
    // The shared runtime helper folds ids to uppercase. Native registry reads
    // use exact keys; restrict its maps first so mixed-case authored ids retain
    // their native miss. A present blank weapon still has a generic whiff tier.
    const pick = <T>(map: Readonly<Record<string, T>>, key: string | null): Record<string, T> =>
        key !== null && Object.prototype.hasOwnProperty.call(map, key) ? { [key]: map[key]! } : {};
    const taxonomy = metadata.audioTaxonomy;
    const input = { items: { item_id: id, item_type: type }, item_layer_types: {
        weapon_classification: wc === '' ? '?' : wc, damage_type: dt, creature_type: value(source, 'creature_type') } };
    const refs = deriveAudioRefs(input, { ...taxonomy, hit: pick(taxonomy.hit, wc), swing: pick(taxonomy.swing, wc),
        damage: pick(taxonomy.damage, dt), spell_miss: pick(taxonomy.spell_miss, dt),
        piercing: dt === taxonomy.piercing ? taxonomy.piercing : '', daggers: wc === taxonomy.daggers ? taxonomy.daggers : '' });
    if (refs && type === 'status_effect' && id !== '') {
        const key = id.toUpperCase();
        if (!metadata.statusBases || !Object.prototype.hasOwnProperty.call(metadata.statusBases, key))
            throw new FromBaseStageMissing('definition-audio-status-registry', id);
        const base = id === key ? metadata.statusBases[key] : null;
        if (base) for (const phase of ['applied', 'removed'] as const)
            refs[phase] = [[`@/${phase}`], [`${base.toUpperCase()}/${phase}`], [`status_${phase}`]];
    }
    const assets = fromBaseRecord(result.assets) ? { ...result.assets } : {};
    delete assets.audio;
    if (refs) assets.audio = { v: 1, ...refs };
    if (Object.keys(assets).length || fromBaseRecord(result.assets)) result.assets = assets;
    return result;
}

// videoField0 / vid_field: video rows use direct scalars, never layer buckets.
const videoField = (row: Row, name: string): unknown => row[name] ?? null;
const columns = ['video_private', 'video_deleted', 'video_created_by_id', 'video_id', 'video_file_extension',
    'video_name', 'video_description', 'video_type', 'video_primary', 'video_order', 'video_attached_id',
    'video_attached_type', 'video_width', 'video_height', 'video_duration', 'video_file_size', 'video_mime_type'];
const paths: Readonly<Record<string, string>> = {
    user: 'user/avatars/', player_character: 'user/player_characters/', campaign_header: 'user/campaign_headers/',
    article_picture: 'user/articles/article_pictures/', source_header: 'user/sources/source_headers/',
    adventure_header: 'user/adventures/adventure_headers/', encounter_location: 'user/encounters/location_pictures/',
    encounter: 'user/encounters/encounter_pictures/', location: 'user/locations/location_pictures/',
    location_map: 'user/locations/location_maps/', creature: 'user/encounter_creatures/encounter_creature_pictures/',
    inventory_picture: 'user/inventory/inventory_pictures/', item: 'items/_base/',
};
const mime: Readonly<Record<string, string>> = {
    mp4: 'video/mp4', m4v: 'video/mp4', mov: 'video/mp4', webm: 'video/webm', ogv: 'video/ogg', ogg: 'video/ogg',
};
const ownString = (map: Readonly<Record<string, string>>, key: string): string | undefined =>
    Object.prototype.hasOwnProperty.call(map, key) ? map[key] : undefined;

/** build_item_arr's own gallery: fetch_videos filters only deleted + attached
 * id, then sorts video_i DESC (attached type is deliberately not a filter). */
export function constructInventoryDefinitionVideos(source: Row, context: InventoryVideoContext): Row {
    if (!Array.isArray(context.rows) || !context.rows.every(fromBaseRecord)
        || !Array.isArray(context.lookupIds) || !context.lookupIds.every(id => typeof id === 'string'))
        throw new FromBaseStageMissing('definition-video-input', 'complete-select');
    if (typeof context.cdnUrl !== 'string') throw new FromBaseStageMissing('definition-videos', 'cdn-url');
    const result = fromBaseClone(source), id = String(readItemField(source, 'item_id') ?? '');
    if (!context.lookupIds.some(held => held.toUpperCase() === id.toUpperCase()))
        throw new FromBaseStageMissing('definition-video-input', id);
    const rows = context.rows.filter(row => Number(videoField(row, 'video_deleted') ?? 0) === 0
        && String(videoField(row, 'video_attached_id') ?? '').toUpperCase() === id.toUpperCase());
    rows.sort((a, b) => Number(videoField(b, 'video_i') ?? 0) - Number(videoField(a, 'video_i') ?? 0));
    const seen = new Set<unknown>(), videos: Row[] = [];
    for (const row of rows) {
        const key = videoField(row, 'video_id');
        if (seen.has(key)) continue;
        seen.add(key);
        const video = Object.fromEntries(columns.map(name => [name, videoField(row, name)]));
        const extension = phpJsonString(videoField(row, 'video_file_extension') ?? '');
        const relative = `videos/${ownString(paths, String(videoField(row, 'video_attached_type'))) ?? 'items/_base/'}${phpJsonString(key)}.${extension}`;
        video.video_url = context.cdnUrl + relative;
        video.video_relative_url = relative;
        video.video_mime_type = ownString(mime, extension.trim().toLowerCase())
            ?? (phpJsonTruthy(videoField(row, 'video_mime_type')) ? videoField(row, 'video_mime_type') : 'video/mp4');
        videos.push(video);
    }
    delete result.item_videos;
    if (videos.length) result.item_videos = videos;
    return result;
}
